/**
 * Promise stock to an order.
 *
 * The panel asks for one thing the order cannot answer: which warehouse the
 * goods come out of. Everything else it already knows, so the lines are
 * pre-filled with what was ordered and the operator only reduces them.
 *
 * A refusal is shown as the rule that stopped it. "Not enough stock in that
 * warehouse" and "more than the customer ordered" are answers somebody can act
 * on; a generic failure is not. A line that cannot be met from one position is
 * refused rather than split — the server says so, and so does this.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { PackageCheck } from "lucide-react";
import { Button, Card, CardContent, CardHeader, CardTitle, Combobox, Input, Label } from "@erp/ui";

import { ApiError } from "@/lib/api/errors";
import { newIdempotencyKey } from "@/features/shared/ops-api";
import { useWarehouseOptions } from "@/features/inventory/queries";

import { allocateFulfilment, type FulfilmentAllocateRequest } from "../fulfilments-api";
import { salesKeys } from "../queries";
import type { SalesOrderLine } from "../api";

export interface AllocatePanelProps {
  salesOrderId: string;
  lines: readonly SalesOrderLine[];
  onAllocated?: (fulfilmentId: string) => void;
}

export function AllocatePanel({ salesOrderId, lines, onAllocated }: AllocatePanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const warehouses = useWarehouseOptions();
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, line.qty_ordered])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const options = useMemo(
    () =>
      (warehouses.data ?? []).map((warehouse) => ({
        value: warehouse.id,
        label: `${warehouse.code} · ${warehouse.name}`,
      })),
    [warehouses.data],
  );

  // A line with no variant is a channel line the catalog never resolved; it
  // cannot be promised because there is nothing to reserve.
  const promisable = lines.filter((line) => line.variant_id !== undefined);

  const allocate = () => {
    if (warehouseId === null) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    const body: FulfilmentAllocateRequest = {
      sales_order_id: salesOrderId,
      warehouse_id: warehouseId,
      lines: promisable
        .filter((line) => (quantities[line.id] ?? "").trim() !== "" && quantities[line.id] !== "0")
        .map((line) => ({
          sales_order_line_id: line.id,
          variant_id: line.variant_id as string,
          qty: (quantities[line.id] ?? line.qty_ordered).trim(),
        })),
    };
    void allocateFulfilment(body, newIdempotencyKey())
      .then(async (created) => {
        setOutcome(
          t("sales.allocate.done", "{{number}} now holds this stock.", {
            number: created.fulfilment.fulfilment_number,
          }),
        );
        await queryClient.invalidateQueries({ queryKey: salesKeys.fulfilments.all });
        onAllocated?.(created.fulfilment.id);
      })
      .catch((caught: unknown) => {
        const code = caught instanceof ApiError ? (caught.code ?? "") : "";
        setError(
          code === ""
            ? t("sales.allocate.error", "The stock could not be promised.")
            : t(`sales.fulfilmentRefusal.${code}`, code),
        );
      })
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{t("sales.allocate.title", "Promise stock")}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t(
            "sales.allocate.hint",
            "Reserves stock for this order. Each line is met from one position in the chosen warehouse; a line that cannot be is refused rather than split.",
          )}
        </p>

        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {outcome === null ? null : (
          <p role="status" className="text-sm text-muted-foreground">
            {outcome}
          </p>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="allocate-warehouse">
            {t("sales.allocate.warehouse", "Out of which warehouse")}
          </Label>
          <Combobox
            id="allocate-warehouse"
            options={options}
            value={warehouseId}
            onChange={setWarehouseId}
            placeholder={t("sales.allocate.pickWarehouse", "Pick a warehouse…")}
            searchPlaceholder={t("actions.search", "Search")}
            emptyMessage={t("sales.allocate.noWarehouses", "No warehouse found.")}
          />
        </div>

        {promisable.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t(
              "sales.allocate.nothingPromisable",
              "No line names a catalogue item, so there is nothing to reserve.",
            )}
          </p>
        ) : (
          <div className="space-y-2">
            {promisable.map((line) => (
              <div key={line.id} className="grid gap-2 sm:grid-cols-[1fr_8rem]">
                <span className="self-center text-sm">
                  <span dir="ltr" className="font-mono text-xs">
                    {line.sku}
                  </span>{" "}
                  {line.description}
                </span>
                <Input
                  dir="ltr"
                  inputMode="decimal"
                  className="text-end font-mono"
                  aria-label={t("sales.allocate.qty", "Quantity to promise")}
                  value={quantities[line.id] ?? ""}
                  onChange={(event) =>
                    setQuantities({ ...quantities, [line.id]: event.target.value })
                  }
                />
              </div>
            ))}
          </div>
        )}

        <Button
          type="button"
          disabled={busy || warehouseId === null || promisable.length === 0}
          onClick={allocate}
        >
          <PackageCheck className="size-4" aria-hidden="true" />
          {t("sales.allocate.action", "Promise it")}
        </Button>
      </CardContent>
    </Card>
  );
}
